import OpenAI from 'openai';
import { notificationService } from './notificationService';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const openaiService = {
  /**
   * Generate AI-powered service recommendations based on client data
   */
  async generateServiceRecommendations(clientData) {
    try {
      const prompt = `You are an expert data strategy consultant analyzing a client's data maturity profile. Based on the following client information, generate personalized service recommendations with ROI predictions.

Client Profile:
- Company: ${clientData?.company || 'Not specified'}
- Current Maturity Score: ${clientData?.currentMaturityScore || 0}/100
- Data Infrastructure Score: ${clientData?.dataInfrastructure || 0}/100
- Analytics Capabilities Score: ${clientData?.analyticsCapabilities || 0}/100
- Governance Maturity Score: ${clientData?.governanceMaturity || 0}/100
- Team Skills Score: ${clientData?.teamSkills || 0}/100
- Strategic Alignment Score: ${clientData?.strategicAlignment || 0}/100
- Recent Consultations: ${clientData?.consultationCount || 0}
- Active Recommendations: ${clientData?.activeRecommendations || 0}
- Industry: ${clientData?.industry || 'Not specified'}

Generate 4-6 prioritized service recommendations that would provide the highest impact for this client. For each recommendation, include:
1. Service name and category
2. Detailed rationale based on their maturity profile
3. Predicted ROI percentage (realistic estimate)
4. ROI confidence level (0-100)
5. Implementation timeline in months
6. Estimated cost range
7. Key success metrics
8. Risk factors

Provide strategic, data-driven recommendations that address their specific gaps and opportunities.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { role: 'system', content: 'You are a senior data strategy consultant with expertise in data maturity assessments, analytics implementation, and ROI analysis. Provide strategic, actionable recommendations with realistic ROI predictions.' },
          { role: 'user', content: prompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'service_recommendations',
            schema: {
              type: 'object',
              properties: {
                recommendations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      serviceName: { type: 'string' },
                      category: { type: 'string' },
                      rationale: { type: 'string' },
                      predictedROI: { type: 'number' },
                      roiConfidence: { type: 'number' },
                      implementationMonths: { type: 'number' },
                      costRange: { type: 'string' },
                      successMetrics: { type: 'array', items: { type: 'string' } },
                      riskFactors: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['serviceName', 'category', 'rationale', 'predictedROI', 'roiConfidence', 'implementationMonths', 'costRange', 'successMetrics', 'riskFactors']
                  }
                },
                overallAnalysis: { type: 'string' },
                priorityFocus: { type: 'string' }
              },
              required: ['recommendations', 'overallAnalysis', 'priorityFocus'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',verbosity: 'high'
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);

      // Store each recommendation in Supabase for real-time notifications
      const aiAnalysis = {
        overallAnalysis: result?.overallAnalysis,
        priorityFocus: result?.priorityFocus
      };

      const storedRecommendations = [];
      for (const rec of result?.recommendations) {
        try {
          const stored = await notificationService?.createRecommendation({
            ...rec,
            aiAnalysis
          });
          storedRecommendations?.push(stored);
        } catch (storeError) {
          console.error('Failed to store recommendation:', storeError);
          // Continue with other recommendations even if one fails
        }
      }

      return result;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating service recommendations:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Analyze client data maturity and provide context
   */
  async analyzeClientMaturity(clientData) {
    try {
      const prompt = `Analyze this client's data maturity profile and provide a comprehensive assessment:

Current Scores:
- Overall Maturity: ${clientData?.currentMaturityScore || 0}/100
- Data Infrastructure: ${clientData?.dataInfrastructure || 0}/100
- Analytics Capabilities: ${clientData?.analyticsCapabilities || 0}/100
- Governance Maturity: ${clientData?.governanceMaturity || 0}/100
- Team Skills: ${clientData?.teamSkills || 0}/100
- Strategic Alignment: ${clientData?.strategicAlignment || 0}/100

Provide:
1. Maturity level classification
2. Key strengths (top 2-3 areas)
3. Critical gaps (top 2-3 areas)
4. Organizational readiness assessment
5. Recommended focus areas for next 6-12 months`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: 'You are a data maturity assessment expert. Provide clear, actionable insights.' },
          { role: 'user', content: prompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'maturity_analysis',
            schema: {
              type: 'object',
              properties: {
                maturityLevel: { type: 'string' },
                strengths: { type: 'array', items: { type: 'string' } },
                gaps: { type: 'array', items: { type: 'string' } },
                readinessScore: { type: 'number' },
                focusAreas: { type: 'array', items: { type: 'string' } }
              },
              required: ['maturityLevel', 'strengths', 'gaps', 'readinessScore', 'focusAreas'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing client maturity:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Generate scenario-based ROI predictions
   */
  async generateROIScenarios(recommendation, clientData) {
    try {
      const prompt = `Generate detailed ROI scenarios for this service recommendation:

Service: ${recommendation?.serviceName}
Base ROI Prediction: ${recommendation?.predictedROI}%
Implementation Timeline: ${recommendation?.implementationMonths} months
Cost Range: ${recommendation?.costRange}

Client Context:
- Current Maturity: ${clientData?.currentMaturityScore}/100
- Company Size: ${clientData?.company || 'Not specified'}

Generate three scenarios (Conservative, Expected, Optimistic) with:
1. ROI percentage
2. Timeline to realize value
3. Key assumptions
4. Success factors`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: 'You are a financial analyst specializing in data strategy ROI calculations.' },
          { role: 'user', content: prompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'roi_scenarios',
            schema: {
              type: 'object',
              properties: {
                conservative: {
                  type: 'object',
                  properties: {
                    roi: { type: 'number' },
                    timelineMonths: { type: 'number' },
                    assumptions: { type: 'array', items: { type: 'string' } },
                    successFactors: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['roi', 'timelineMonths', 'assumptions', 'successFactors']
                },
                expected: {
                  type: 'object',
                  properties: {
                    roi: { type: 'number' },
                    timelineMonths: { type: 'number' },
                    assumptions: { type: 'array', items: { type: 'string' } },
                    successFactors: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['roi', 'timelineMonths', 'assumptions', 'successFactors']
                },
                optimistic: {
                  type: 'object',
                  properties: {
                    roi: { type: 'number' },
                    timelineMonths: { type: 'number' },
                    assumptions: { type: 'array', items: { type: 'string' } },
                    successFactors: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['roi', 'timelineMonths', 'assumptions', 'successFactors']
                }
              },
              required: ['conservative', 'expected', 'optimistic'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating ROI scenarios:', error);
      }
      throw new Error(errorInfo.message);
    }
  }
};